import { createContext, useContext, useEffect } from 'react';
import { useUser, useAuth as useClerkAuth, useClerk } from '@clerk/clerk-react';

interface AuthContextType {
  user: any;
  loading: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const { user, isLoaded } = useUser();
  const { signOut: clerkSignOut } = useClerk();

  // Store user info in localStorage for workflowApi access
  useEffect(() => {
    if (isLoaded && user) {
      localStorage.setItem('clerk_authenticated', 'true');
      localStorage.setItem('clerk_user_id', user.id);
    } else if (isLoaded && !user) {
      localStorage.removeItem('clerk_authenticated');
      localStorage.removeItem('clerk_user_id');
    }
  }, [user, isLoaded]);

  const signOut = async () => {
    localStorage.removeItem('clerk_authenticated');
    localStorage.removeItem('clerk_user_id');
    await clerkSignOut();
  };

  return (
    <AuthContext.Provider value={{ 
      user: user ? {
        id: user.id,
        email: user.primaryEmailAddress?.emailAddress || '',
        user_metadata: {
          full_name: user.fullName || user.firstName || ''
        }
      } : null, 
      loading: !isLoaded, 
      signOut 
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};